import { useState, useEffect, useCallback, useRef } from 'react';
import { debounce } from 'lodash';
import { saveProject, updateProject } from '../utils/supabaseClient';
import { useAuth } from '../contexts/AuthContext';

/**
 * Hook to automatically save project data to Supabase
 * @param {boolean} enabled - Whether auto-save is enabled
 * @param {string} projectId - Project ID (if updating an existing project)
 * @param {Function} saveFunction - Custom function to call for saving
 * @returns {Object} - Save status and last saved timestamp
 */
export const useAutoSave = (enabled = true, projectId = null, saveFunction = null) => {
  const [saveStatus, setSaveStatus] = useState('saved'); // 'saved', 'saving', 'error'
  const [lastSaved, setLastSaved] = useState(null);
  const [debugMessage, setDebugMessage] = useState('');
  const { user } = useAuth();
  
  // Create a save function (not debounced)
  const saveProjectData = useCallback(async () => {
    try {
      // Don't save if user is not authenticated or auto-save is disabled
      if (!user || !enabled) {
        return null;
      }

      setSaveStatus('saving');
      setDebugMessage('');
      
      let result;
      if (saveFunction) {
        // Use the provided save function
        result = await saveFunction();
      } else if (projectId) {
        // Default behavior - update existing project
        result = await updateProject(projectId, {});
      } else {
        // We shouldn't get here, but just in case - create new project
        console.warn('Auto-save attempted without projectId or saveFunction');
        return null;
      }
      
      setSaveStatus('saved');
      const timestamp = new Date();
      setLastSaved(timestamp);
      
      // More visible log message for successful saves
      console.log('%c✅ SAVED TO SUPABASE! Project saved successfully at ' + timestamp.toLocaleTimeString(), 
        'background: #4CAF50; color: white; font-weight: bold; padding: 2px 6px; border-radius: 2px;', {
        projectId,
        timestamp: timestamp.toLocaleTimeString()
      });
      
      return result;
    } catch (error) {
      console.error('Auto-save error:', error);
      setSaveStatus('error');
      setDebugMessage('');
      throw error;
    }
  }, [projectId, user, enabled, saveFunction]);

  // Create a debounced save function for change-based saves (like window resize)
  const debouncedSave = useCallback(
    debounce(saveProjectData, 2000), // 2000ms debounce
    [saveProjectData]
  );

  // Handle browser resize events - this ensures that after resizing
  // the browser window, any changes to the viewport are saved
  useEffect(() => {
    if (!enabled || !user) return;
    
    const handleResize = () => {
      // Only trigger save if we're already past initial loading
      if (lastSaved) {
        debouncedSave();
      }
    };
    
    window.addEventListener('resize', handleResize);
    
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [enabled, user, debouncedSave, lastSaved]);

  // Set up interval timer for periodic saves
  useEffect(() => {
    // Only set up interval if we have a project to save and auto-save is enabled
    if (!projectId || !user || !enabled) return;
    
    const SAVE_INTERVAL = 30 * 1000; // 30 seconds
    
    const intervalId = setInterval(() => {
      if (user && enabled) {
        saveProjectData();
      }
    }, SAVE_INTERVAL);
    
    // Clean up interval on unmount
    return () => clearInterval(intervalId);
  }, [projectId, user, enabled, saveProjectData]);

  // Expose a way to manually trigger a save
  const triggerSave = useCallback(() => {
    return saveProjectData();
  }, [saveProjectData]);

  return { 
    saveStatus, 
    lastSaved, 
    debugMessage,
    triggerSave
  };
}; 